/**
 * Everything the React components did in the browser, as plain JS.
 *
 * Sources, one per section below:
 *   Reveal.tsx · Nav.tsx · CartDrawer.tsx + cart.tsx · ProductView.tsx +
 *   Gallery.tsx + BuyBox.tsx + BundleUpsell.tsx
 *
 * All listeners are delegated from `document`, because the cart drawer's
 * markup is replaced wholesale on every change (Section Rendering API) and
 * anything bound directly to its nodes would die on the first update.
 */
(function () {
  "use strict";

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";

  function money(cents) {
    // formatPrice() from lib/format.ts: whole dollars drop the ".00".
    var dollars = cents / 100;
    return dollars % 1 === 0 ? "$" + dollars.toFixed(0) : "$" + dollars.toFixed(2);
  }

  /* ─── Reveal.tsx ────────────────────────────────────────────────────────
     Moved inline into layout/theme.liquid (2026-10-01) so it doesn't wait
     on this deferred file. The animation itself is .reveal in theme.css. */

  /* ─── Nav.tsx ──────────────────────────────────────────────────────────
     Escape closes the sheet, matching the cart drawer. A tap anywhere
     outside closes it too — without this the only way out is the button
     itself, which is not where anyone reaches first. */
  function initNav() {
    var nav = document.querySelector("[data-nav]");
    if (!nav) return;
    var toggle = nav.querySelector("[data-menu-toggle]");
    var sheet = nav.querySelector("[data-menu-sheet]");
    var iconOpen = nav.querySelector("[data-menu-icon-open]");
    var iconClose = nav.querySelector("[data-menu-icon-close]");
    if (!toggle || !sheet) return;

    function setOpen(open) {
      sheet.hidden = !open;
      toggle.setAttribute("aria-expanded", String(open));
      toggle.setAttribute("aria-label", open ? "Close menu" : "Open menu");
      if (iconOpen) iconOpen.hidden = open;
      if (iconClose) iconClose.hidden = !open;
    }

    toggle.addEventListener("click", function () {
      setOpen(sheet.hidden);
    });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape") setOpen(false);
    });
    document.addEventListener("pointerdown", function (e) {
      if (!sheet.hidden && !nav.contains(e.target)) setOpen(false);
    });
  }

  /* ─── CartDrawer.tsx + cart.tsx ────────────────────────────────────────
     The cart itself lives on Shopify's server now, so this is only the
     drawer's open/closed state plus Ajax calls. There is no localStorage
     copy to keep in sync and nothing to clear at hand-off. */
  var cartOpen = false;

  function cartNodes() {
    return {
      panel: document.querySelector("[data-cart-panel]"),
      overlay: document.querySelector("[data-cart-overlay]"),
    };
  }

  function setCartOpen(open) {
    var n = cartNodes();
    if (!n.panel || !n.overlay) return;
    cartOpen = open;

    n.panel.classList.toggle("translate-x-full", !open);
    n.panel.classList.toggle("translate-x-0", open);
    n.overlay.classList.toggle("opacity-0", !open);
    n.overlay.classList.toggle("pointer-events-none", !open);
    n.overlay.classList.toggle("opacity-100", open);

    if (open) n.panel.removeAttribute("inert");
    else n.panel.setAttribute("inert", "");

    // Lock page scroll while the drawer is open.
    document.documentElement.style.overflow = open ? "hidden" : "";
  }

  function showCartError(message) {
    var el = document.querySelector("[data-cart-error]");
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
  }

  /** Re-render the drawer from the server and re-apply the open state. */
  function refreshCart() {
    return fetch(root + "?sections=cart-drawer", { headers: { Accept: "application/json" } })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var html = data["cart-drawer"];
        if (!html) return;
        var current = document.querySelector("[data-cart-root]");
        if (!current) return;
        var parsed = new DOMParser().parseFromString(html, "text/html");
        var next = parsed.querySelector("[data-cart-root]");
        if (!next) return;
        current.replaceWith(next);
        setCartOpen(cartOpen);
        return fetch(root + "cart.js", { headers: { Accept: "application/json" } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var badge = document.querySelector("[data-cart-count]");
            if (badge) {
              badge.textContent = cart.item_count;
              badge.hidden = cart.item_count === 0;
            }
          });
      });
  }

  function addToCart(variantId, quantity) {
    return fetch(root + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: variantId, quantity: quantity }] }),
    }).then(function (res) {
      if (!res.ok) {
        return res.json().then(function (body) {
          throw new Error(body.description || body.message || "Could not add that to the cart.");
        });
      }
      return res.json();
    });
  }

  /* Used by the homepage's Build-a-box (v5): several toys in one request,
     then the same drawer refresh/open every other add goes through. */
  window.PLA = {
    addItems: function (items) {
      return fetch(root + "cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: items }),
      })
        .then(function (res) {
          if (!res.ok) {
            return res.json().then(function (body) {
              throw new Error(body.description || body.message || "Could not add that to the cart.");
            });
          }
          return res.json();
        })
        .then(refreshCart)
        .then(function () { setCartOpen(true); })
        .catch(function (err) { setCartOpen(true); showCartError(err.message); });
    },
  };

  function changeLine(line, quantity) {
    return fetch(root + "cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ line: line, quantity: quantity }),
    }).then(function (res) { return res.json(); });
  }

  function initCartDelegation() {
    document.addEventListener("click", function (e) {
      var open = e.target.closest("[data-cart-open]");
      if (open) { setCartOpen(true); return; }

      var close = e.target.closest("[data-cart-close], [data-cart-overlay]");
      if (close) { setCartOpen(false); return; }

      // v5: swap a whole set of singles for the set itself (cart drawer).
      var swap = e.target.closest("[data-swap-set]");
      if (swap) {
        swap.disabled = true;
        fetch(root + "cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ updates: JSON.parse(swap.getAttribute("data-updates")) }),
        })
          .then(function () { return addToCart(parseInt(swap.getAttribute("data-set-variant"), 10), 1); })
          .then(refreshCart)
          .catch(function (err) { refreshCart(); showCartError(err.message); });
        return;
      }

      var change = e.target.closest("[data-cart-change]");
      if (change) {
        var line = parseInt(change.getAttribute("data-line"), 10);
        var qty = parseInt(change.getAttribute("data-qty"), 10);
        changeLine(line, qty).then(refreshCart);
      }
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && cartOpen) setCartOpen(false);
    });
  }

  /* ─── ProductView + Gallery + BuyBox + BundleUpsell ────────────────────
     One state object, because in React these four shared it through props.
     `asBundle` swaps what gets added to the cart, the price, the gallery and
     the description — bundles aren't separate listings. */
  function initProduct() {
    var page = document.querySelector("[data-product]");
    if (!page) return;

    var configEl = page.parentNode.querySelector("[data-product-config]")
      || document.querySelector("[data-product-config]");
    var mapEl = page.querySelector("[data-gallery-map]");
    if (!configEl) return;

    var config = JSON.parse(configEl.textContent);
    var galleryMap = mapEl ? JSON.parse(mapEl.textContent) : {};

    var state = {
      variantId: config.initialVariantId,
      priceCents: config.initialPriceCents,
      qty: 1,
      // The homepage bundle cards link here with ?bundle=1, so the page opens
      // on the bundle the customer just clicked instead of the single.
      asBundle: !!config.bundle && /[?&]bundle=1(&|$)/.test(window.location.search),
    };

    var mainImg = page.querySelector("[data-gallery-main]");
    var thumbs = page.querySelector("[data-gallery-thumbs]");
    var priceEl = page.querySelector("[data-price]");
    var bundleTag = page.querySelector("[data-bundle-price-tag]");
    var addLabel = page.querySelector("[data-add-label]");
    var qtyEl = page.querySelector("[data-qty]");
    var optionBlock = page.querySelector("[data-option-block]");
    var optionCurrent = page.querySelector("[data-option-current]");
    var singleDesc = page.querySelector("[data-single-description]");
    var bundleDesc = page.querySelector("[data-bundle-description]");
    var shippingNote = page.querySelector("[data-shipping-note]");
    var shippingNoteBundle = page.querySelector("[data-shipping-note-bundle]");
    var addBtn = page.querySelector("[data-add-to-cart]");
    var singleBtn = page.querySelector("[data-choose-single]");
    var bundleBtn = page.querySelector("[data-choose-bundle]");
    var singleDot = page.querySelector("[data-single-dot]");
    var bundleDot = page.querySelector("[data-bundle-dot]");

    var CHECK = '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 12.5l5 5L20 7"/></svg>';

    var justAddedTimer = null;

    function currentImages() {
      if (state.asBundle && config.bundle) return config.bundle.images;
      return galleryMap[state.variantId] || config.initialImages || [];
    }

    function sized(url, width) {
      // Shopify CDN sizing, the equivalent of next/image's optimizer.
      // Liquid already emits these at width=1200, so rewrite that parameter
      // rather than appending a second one — two `width=` keys made the CDN
      // reject the URL outright and every image came back broken.
      if (/[?&]width=\d+/.test(url)) {
        return url.replace(/([?&])width=\d+/, "$1width=" + width);
      }
      return url.indexOf("?") === -1
        ? url + "?width=" + width
        : url + "&width=" + width;
    }

    function renderGallery() {
      var images = currentImages();
      if (!images.length) return;
      var active = 0;

      function paint() {
        if (mainImg) mainImg.src = sized(images[active], 1200);
        if (!thumbs) return;
        thumbs.innerHTML = "";
        if (images.length <= 1) return;
        images.forEach(function (img, i) {
          var b = document.createElement("button");
          b.type = "button";
          b.setAttribute("aria-label", "View photo " + (i + 1));
          b.setAttribute("aria-pressed", String(i === active));
          b.className =
            "relative h-16 w-16 overflow-hidden rounded-xl bg-white transition-all duration-300 ease-fluid " +
            (i === active ? "ring-2 ring-ink" : "opacity-70 ring-1 ring-ink/10 hover:opacity-100");
          b.innerHTML = '<img src="' + sized(img, 128) + '" alt="" class="fill-img">';
          b.addEventListener("click", function () {
            active = i;
            paint();
          });
          thumbs.appendChild(b);
        });
      }
      paint();
    }

    function render() {
      var price = state.asBundle && config.bundle ? config.bundle.priceCents : state.priceCents;

      if (priceEl) priceEl.textContent = money(price);
      if (bundleTag) bundleTag.hidden = !state.asBundle;
      if (qtyEl) qtyEl.textContent = state.qty;
      if (addLabel && !justAddedTimer) addLabel.textContent = "Add to cart · " + money(price * state.qty);

      if (singleDesc) singleDesc.hidden = state.asBundle;
      if (bundleDesc) bundleDesc.hidden = !state.asBundle;
      // Bundles have no colour choice of their own.
      if (optionBlock) optionBlock.hidden = state.asBundle;
      if (shippingNote) shippingNote.hidden = state.asBundle;
      if (shippingNoteBundle) shippingNoteBundle.hidden = !state.asBundle;

      if (singleBtn && bundleBtn) {
        singleBtn.setAttribute("aria-pressed", String(!state.asBundle));
        bundleBtn.setAttribute("aria-pressed", String(state.asBundle));
        singleBtn.className =
          "flex w-full items-center gap-3 rounded-[1.25rem] px-4 py-3.5 text-left transition-all duration-300 ease-fluid active:scale-[0.99] " +
          (state.asBundle ? "bg-white/60 ring-1 ring-ink/10 hover:ring-ink/30" : "bg-white ring-2 ring-ink");
        bundleBtn.className =
          "group relative flex w-full items-center gap-3 overflow-hidden rounded-[1.25rem] px-4 py-3.5 text-left transition-all duration-300 ease-fluid active:scale-[0.99] " +
          (state.asBundle ? "bg-white ring-2 ring-accent" : "bg-white/60 ring-1 ring-accent/30 hover:ring-accent/60");
        singleDot.className =
          "flex h-5 w-5 shrink-0 items-center justify-center rounded-full ring-1 " +
          (state.asBundle ? "ring-ink/20" : "bg-ink ring-ink");
        bundleDot.className =
          "flex h-5 w-5 shrink-0 items-center justify-center rounded-full ring-1 " +
          (state.asBundle ? "bg-accent ring-accent" : "ring-ink/20");
        singleDot.innerHTML = state.asBundle ? "" : CHECK;
        bundleDot.innerHTML = state.asBundle ? CHECK : "";
      }

      renderGallery();
      syncStickyBar();
    }

    page.addEventListener("click", function (e) {
      var opt = e.target.closest("[data-option-btn]");
      if (opt) {
        state.variantId = parseInt(opt.getAttribute("data-variant-id"), 10);
        state.priceCents = parseInt(opt.getAttribute("data-variant-price"), 10);
        if (optionCurrent) optionCurrent.textContent = opt.getAttribute("data-variant-title");
        page.querySelectorAll("[data-option-btn]").forEach(function (b) {
          var on = b === opt;
          b.setAttribute("aria-pressed", String(on));
          b.className =
            "flex items-center gap-2 rounded-full px-3.5 py-2 text-sm transition-all duration-300 ease-fluid active:scale-[0.97] " +
            (on ? "bg-white font-semibold ring-2 ring-ink" : "bg-white/60 ring-1 ring-ink/10 hover:ring-ink/30");
        });
        render();
        return;
      }

      if (e.target.closest("[data-qty-up]")) { state.qty = Math.min(20, state.qty + 1); render(); return; }
      if (e.target.closest("[data-qty-down]")) { state.qty = Math.max(1, state.qty - 1); render(); return; }
      if (e.target.closest("[data-choose-single]")) { state.asBundle = false; render(); return; }
      if (e.target.closest("[data-choose-bundle]")) { state.asBundle = true; render(); return; }
      if (e.target.closest("[data-add-to-cart]")) { handleAdd(); }
    });

    function handleAdd() {
      var id = state.asBundle && config.bundle ? config.bundle.variantId : state.variantId;
      showCartError("");
      addToCart(id, state.qty)
        .then(function () {
          if (addLabel) {
            addLabel.textContent = "Added to cart ✓";
            if (addBtn) { addBtn.classList.add("bg-accent"); addBtn.classList.remove("bg-ink"); }
            clearTimeout(justAddedTimer);
            justAddedTimer = setTimeout(function () {
              justAddedTimer = null;
              if (addBtn) { addBtn.classList.remove("bg-accent"); addBtn.classList.add("bg-ink"); }
              render();
            }, 1600);
          }
          return refreshCart();
        })
        .then(function () { setCartOpen(true); })
        .catch(function (err) { setCartOpen(true); showCartError(err.message); });
    }

    /* The mobile sticky add bar.
       On a phone the real button sits below the fold, so a copy of it rides
       along the bottom of the screen — but only once the real one is out of
       sight, otherwise the page shows two identical buttons at once.
       Appended to <body> for the same reason the React version portalled it:
       an ancestor with a transform or filter makes `position: fixed` resolve
       against that ancestor instead of the viewport. */
    var stickyBar = null;
    var inlineVisible = true;

    function buildStickyBar() {
      if (!addBtn) return;
      stickyBar = document.createElement("div");
      stickyBar.className =
        "fixed inset-x-0 bottom-0 z-30 translate-y-full pointer-events-none transition-transform duration-300 ease-fluid lg:hidden motion-reduce:transition-none";
      stickyBar.setAttribute("aria-hidden", "true");
      stickyBar.innerHTML =
        '<div class="border-t border-ink/5 bg-cream/90 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur-xl">' +
        '<button type="button" data-sticky-add tabindex="-1" class="group flex w-full items-center justify-between rounded-full bg-ink py-1.5 pl-6 pr-1.5 text-sm font-semibold text-white transition-colors duration-300 ease-fluid active:scale-[0.98]">' +
        '<span data-sticky-label></span>' +
        '<span class="flex h-10 w-10 items-center justify-center rounded-full bg-white/15">' +
        '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>' +
        "</span></button></div>";
      document.body.appendChild(stickyBar);
      stickyBar.querySelector("[data-sticky-add]").addEventListener("click", handleAdd);

      if ("IntersectionObserver" in window) {
        new IntersectionObserver(
          function (entries) {
            inlineVisible = entries[0].isIntersecting;
            syncStickyBar();
          },
          { rootMargin: "-8px 0px -8px 0px" }
        ).observe(addBtn);
      }
    }

    function syncStickyBar() {
      if (!stickyBar) return;
      var label = stickyBar.querySelector("[data-sticky-label]");
      if (label && addLabel) label.textContent = addLabel.textContent;
      stickyBar.setAttribute("aria-hidden", String(inlineVisible));
      stickyBar.classList.toggle("translate-y-full", inlineVisible);
      stickyBar.classList.toggle("pointer-events-none", inlineVisible);
      stickyBar.classList.toggle("translate-y-0", !inlineVisible);
      var btn = stickyBar.querySelector("[data-sticky-add]");
      if (btn) btn.tabIndex = inlineVisible ? -1 : 0;
    }

    buildStickyBar();
    render();
  }

  function init() {
    initNav();
    initCartDelegation();
    initProduct();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
